import { API_BASE } from './api-base';

/**
 * ONE way to fetch a remote document's body from the browser — the direct request, with the pod as the
 * fallback the direct request sometimes needs.
 *
 * WHY A FALLBACK AT ALL. A cross-origin `fetch` requires EVERY response in the redirect chain to carry
 * `Access-Control-Allow-Origin`, not just the last one. `links.wixel.com/link/<id>/raw` — the URL a
 * Wixel-generated PDF, deck or doc is attached as — answers 302 to a wixmp object that does send `*`, while
 * the 302 itself sends nothing. So the browser blocks at hop 1 and never reaches bytes it would have been
 * allowed to read: the document neither previews nor downloads, and the link is not expired, revoked or
 * wrong. A server has no same-origin policy to satisfy, so the pod re-fetches and hands the bytes back
 * (`pages/api/document-bytes.ts`, same SSRF allowlist as the Office converter).
 *
 * WHY NOT ALWAYS GO THROUGH THE POD. Most Wix media hosts answer the browser directly and fine; routing
 * every document through the pod would put every byte of every preview on our own egress for the sake of one
 * host's redirect. The direct attempt stays the normal path and costs nothing when it works.
 *
 * WHY THIS CANNOT BE A `try`/`catch` AT EACH CALL SITE. There were three of them (PDF bytes, document text,
 * download) and only one had a fallback — `window.open`, from an async `catch`, i.e. after the click's
 * user-gesture window has closed, which the popup blocker eats. That is why "Download does nothing" was the
 * reported symptom rather than an error.
 */

/** The proxy route. Exported for the test that pins the request shape.
 *
 *  ⚠️ **The URL travels in the BODY, never in this path.** A Wix media URL is a bearer credential — its
 *  `token=` JWT authorizes the document until it expires — and `express-server.js` logs `req.originalUrl`
 *  for every response over 400, which includes the 413/502/504 this route answers by design. A `?url=`
 *  form would therefore have written a live, replayable document token into app logs on exactly the paths
 *  that fail. `convert-document` POSTs its URL for the same reason. */
export const documentProxyPath = `${API_BASE}/api/document-bytes`;

/**
 * Fetches `url` directly; on a refusal the browser reports as a bare `TypeError` (CORS, or offline — they
 * are indistinguishable to script by design), retries through the pod.
 *
 * `credentials: 'omit'` and `referrerPolicy: 'no-referrer'` on the direct request are load-bearing, for the
 * reasons `fetchDocText` documents: a Wix media URL is token-signed, so sending `wixSession2` hands our
 * session to a request that does not need it, and the URL we came from is itself signed.
 *
 * A non-`ok` direct response is returned AS IS rather than retried. The pod fetches the same URL with the
 * same token, so a 403 or 404 there is a 403 or 404 here — retrying only turns one honest error into two
 * requests and a slower error state.
 */
export async function fetchRemoteDocument(url: string): Promise<Response> {
  try {
    return await fetch(url, { credentials: 'omit', referrerPolicy: 'no-referrer' });
  } catch {
    return fetch(documentProxyPath, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
  }
}

/** The proxy's refusal for a document over `MAX_DOCUMENT_BYTES`, which the DIRECT path expresses as a
 *  `DocTooLargeError` off its own bounded read.
 *
 *  Left as a bare `HTTP 413` the two paths disagree about the same document: the viewer would show its
 *  RETRYABLE error state for a file that can never fit, and the retry re-issues the identical request. The
 *  caller maps it back so an over-cap document keeps the download hand-off on both paths. */
export const PROXY_TOO_LARGE_STATUS = 413;
